import { useElementSize } from '@mantine/hooks'
import { Fragment, type ReactNode, useState } from 'react'
import { twMerge } from 'tailwind-merge'

export type InputMatrixProps = {
  columns: ReactNode[]
  rows: ReactNode[]
  renderCell: (rowIndex: number, columnIndex: number) => ReactNode
  renderColumnAction?: (columnIndex: number) => ReactNode
  renderRowAction?: (rowIndex: number) => ReactNode
}

const stickyStyle = 'sticky z-10 bg-white ltr:left-0 rtl:right-0'

const rowStyles: Record<'start' | 'middle' | 'end', string> = {
  start: 'border-colorBorder border-1 rounded-s-[12px] border-e-0 min-h-11',
  middle: 'border-colorBorder border-1 border-x-0 min-h-11',
  end: 'border-colorBorder border-1 rounded-e-[12px] border-s-0 min-h-11',
}

export const InputMatrix = ({
  columns,
  rows,
  renderCell,
  renderColumnAction,
  renderRowAction,
}: InputMatrixProps) => {
  const [hover, setHover] = useState<{
    columnIndex?: number
    rowIndex?: number
  }>()
  const { ref, width } = useElementSize()
  const w = `${width / 3}px`

  return (
    <div className='overflow-x-auto max-w-full' ref={ref}>
      <div
        className='grid gap-y-2 w-fit min-w-full'
        style={{
          gridTemplateColumns: `minmax(${w}, 1fr) repeat(${columns.length}, minmax(min-content, 1fr)) ${renderRowAction ? 'minmax(min-content, max-content)' : ''}`,
        }}
      >
        {/* Those empty divs are to correctly align the grid */}
        <div className={stickyStyle} />
        {columns.map((column, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: Static array with stable order
            key={index}
            className='text-center px-3 whitespace-nowrap min-w-0'
            onMouseEnter={() =>
              setHover({
                columnIndex: index,
                rowIndex: undefined,
              })
            }
            onMouseLeave={() => setHover(undefined)}
          >
            {renderColumnAction && (
              <div
                className={twMerge(
                  'transition-all pointer-events-none opacity-0',
                  hover?.columnIndex === index && 'pointer-events-auto opacity-100'
                )}
              >
                {renderColumnAction?.(index)}
              </div>
            )}
            {column}
          </div>
        ))}
        {renderRowAction && <div />}
        {rows.map((row, rowIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Static array with stable order
          <Fragment key={rowIndex}>
            <div className={stickyStyle}>
              <div
                className={twMerge(rowStyles.start, 'flex items-center ps-4 pr-3 min-w-0')}
                onMouseEnter={() => setHover({ columnIndex: undefined, rowIndex })}
                onMouseLeave={() => setHover(undefined)}
              >
                <div className='wrap-anywhere'>{row}</div>
              </div>
            </div>
            {columns.map((_, columnIndex) => {
              const isLastColumn = columnIndex === columns.length - 1
              const isEndColumn = isLastColumn && !renderRowAction
              return (
                <div
                  /* biome-ignore lint/suspicious/noArrayIndexKey: Matrix cells are in fixed positions with stable indices */
                  key={columnIndex}
                  className={twMerge(
                    rowStyles[isEndColumn ? 'end' : 'middle'],
                    'flex items-center justify-center px-3 min-w-0'
                  )}
                  onMouseEnter={() => setHover({ columnIndex, rowIndex })}
                  onMouseLeave={() => setHover(undefined)}
                >
                  <div className='w-full flex justify-center whitespace-nowrap'>
                    {renderCell(rowIndex, columnIndex)}
                  </div>
                </div>
              )
            })}
            {renderRowAction && (
              <div
                className={twMerge(rowStyles.end, 'flex items-center justify-center pe-4')}
                onMouseEnter={() => setHover({ columnIndex: undefined, rowIndex })}
                onMouseLeave={() => setHover(undefined)}
              >
                <div
                  className={twMerge(
                    'transition-all pointer-events-none opacity-0',
                    hover?.rowIndex === rowIndex && 'pointer-events-auto opacity-100'
                  )}
                >
                  {renderRowAction(rowIndex)}
                </div>
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}
